/* eslint-disable import/no-cycle */
import Grp from './Grp.js';
import MessagesFactory from './Messages.js';
import Utils from '../Utils.js';
import Constants from '../Constants.js';
import InterfaceActions from './InterfaceActions.js';
import Access from './InterfaceReducer.js';
import Rule from '../rule/Rule.js';
import Rules from '../rule/Rules.js';
import Rsrc from '../rsrc/Rsrc.js';
import ResourceUser from '../users/ResourceUser.js';
import ResourceUsers from '../users/ResourceUsers.js';
import ResourceTemporaryUser from '../temporaryUsers/ResourceTemporaryUser.js';
import ResourceTemporaryUsers from '../temporaryUsers/ResourceTemporaryUsers.js';
import ApiAccessKey from '../apiAccessKey/ApiAccessKey.js';
import ApiAccessKeys from '../apiAccessKey/ApiAccessKeys.js';
import Can from './Can.js';
const {
  getId
} = Utils;

/**
 * @Class Obj - class representing a Single device
 */
class Obj {
  /**
   * creates an Object
   * @param {string} parentPath the path of the api call for the immediate parent
   * @param {string} id identifier for the group to be queried for
   * @param {boolean} isInGroup represents if the object is in a group
   */
  constructor(parentPath, id, isInGroup) {
    this.id = getId(id);
    this.isInGroup = isInGroup;
    this.parentPath = parentPath;
    this.path = `${parentPath}/${this.id}`;
  }
  can() {
    return Can.create(this.path, false, false);
  }

  /**
   * makes an api call for the for the Object at the path
   * @param {object} options api options
   * @returns {function} redux thunk function
   **/
  get(options, queryName) {
    return InterfaceActions.get(this.path, this.isInGroup ? this.parentPath : null, options, queryName);
  }

  /**
   * makes an api call for the list of objects at a path
   * this is relevant for an obj when there is a * in the path
   * making this return a set of table results
   * @param {object} options api options
   * @returns {function} redux thunk function
   **/
  list(options, queryName) {
    /**
     * @returns {Promise} represents the result of the api call for the list
     */
    return InterfaceActions.list(this.path, this.id, Constants.INTERFACE_LIST, options, queryName);
  }

  /**
   * makes an api call for the list of objects at a path
   * this is relevant for an obj when there is a * in the path
   * making this return a set of table results
   * @param {object} obj the search to execute
   * @param {object} options api options
   * @returns {function} redux thunk function
   **/
  search(obj, options, queryName) {
    /**
     * @returns {Promise} represents the result of the api call for the list
     */
    return InterfaceActions.search(`${this.path}.search`, obj, this.id, Constants.INTERFACE_LIST, options, queryName);
  }

  /**
   * Returns a action to perform a getDeviceByNetworkAlias
   */
  getDeviceByNetworkAlias(network, key, value, options) {
    return InterfaceActions.getDeviceByNetworkAlias(this.path, network, key, value, options);
  }

  /**
   * makes an api call to get the history of an object at the path
   * @param {object} query.query query params that the server will use
   * @param {string} query.queryName query identifier
   * @param {object} options api options
   * @returns {function} redux thunk function
   */
  history(query, options) {
    return InterfaceActions.getHistory(`${this.path}.history`, this.parentPath, {
      ...options,
      q: JSON.stringify(query.query)
    }, query.queryName);
  }
  events(query, options) {
    return InterfaceActions.getHistory(`${this.path}.events`, this.parentPath, {
      ...options,
      q: JSON.stringify(query.query)
    }, query.queryName);
  }

  /**
   * makes an api call to create an object at the path
   * @param {object} obj the object to be created
   * @param {object} options api options
   * @returns {function} redux thunk function
   **/
  create(obj, options) {
    return InterfaceActions.create(this.path, obj, this.id, Constants.INTERFACE_CREATE_SET, options);
  }

  /**
   * makes an api call to update the object at the path
   * @param {object} obj the object values to be updated
   * @param {object} options api options
   * @returns {function} redux thunk function
   **/
  update(obj, options) {
    return InterfaceActions.update(this.path, obj, this.parentPath, options, Constants.INTERFACE_UPDATE);
  }

  /**
   * makes an api call to delete the object at the path
   * @param {object} options api options
   * @returns {function} redux thunk function
   **/
  delete(body, options, reducerOptions) {
    return InterfaceActions.remove(this.parentPath, this.id, body, options, reducerOptions);
  }

  /**
   * api call to get the model for the path
   * @param {object} options api options
   * @return {function} redux thunk function
   */
  model(options) {
    return InterfaceActions.get(`${this.path}.model`, this.isInGroup ? this.parentPath : null, options);
  }

  /**
   * locally filters the results (only usable in obj for tables)
   * @param {object} filter the filter object for the path
   */
  filter(filter) {
    return {
      type: Constants.INTERFACE_FILTER,
      filter,
      path: this.path
    };
  }

  /**
   * @param {string} id id of an obj
   * @returns {Obj} object with the current path and the id provided
   */
  obj(id) {
    return new Obj(this.path, id);
  }

  /**
   * @param {string} attribute the attribute representing a group on the current object
   * @returns {Grp} group class with the current path and attribute
   */
  grp(attribute) {
    return Grp.create(this.parentPath, this.id, attribute);
  }
  getResourcesApi = (dispatch, getState, opts, resourceId) => {
    const {
      api
    } = opts;
    const p = this.path.split('/');
    const toReturn = api.interface(p[0], p[1]);
    return toReturn.obj(p.slice(2).join('/'));
  };
  getUsersApi = (dispatch, getState, opts, resourceId) => {
    return this.getResourcesApi(dispatch, getState, opts, resourceId);
  };
  getRuleApi = (dispatch, getState, opts, resourceId) => {
    return this.getResourcesApi(dispatch, getState, opts, resourceId);
  };
  getRsrcApi = (dispatch, getState, opts, resourceId) => {
    return this.getResourcesApi(dispatch, getState, opts, resourceId);
  };
  rsrc(path) {
    return new Rsrc({
      ownerId: this.id,
      coownerId: null,
      path,
      resourceApi: this.getRsrcApi
    });
  }
  apiAccessKey(apiAccessKeyId) {
    return new ApiAccessKey({
      apiAccessKeyId,
      resourceId: this.id,
      resourceApi: this.getUsersApi
    });
  }
  apiAccessKeys() {
    return new ApiAccessKeys({
      resourceId: this.id,
      resourceApi: this.getUsersApi
    });
  }
  users() {
    return new ResourceUsers({
      resourceId: this.id,
      resourceApi: this.getUsersApi
    });
  }
  user(userId) {
    return new ResourceUser({
      userId,
      resourceId: this.id,
      resourceApi: this.getUsersApi
    });
  }
  temporaryUser(userId) {
    return new ResourceTemporaryUser({
      userId,
      resourceId: this.id,
      resourceApi: this.getUsersApi
    });
  }
  temporaryUsers() {
    return new ResourceTemporaryUsers({
      resourceId: this.id,
      resourceApi: this.getUsersApi
    });
  }
  rules() {
    return new Rules({
      resourceId: this.id,
      resourceType: 'device',
      resourceApi: this.getRuleApi
    });
  }
  rule(ruleId) {
    return new Rule({
      ruleId,
      resourceId: this.id,
      resourceType: 'device',
      resourceApi: this.getRuleApi
    });
  }

  /**
   * makes an api access object for managing a device's outbound messages
   * @returns {object} the api access object
   */
  messages() {
    return MessagesFactory.create(this.parentPath, this.id);
  }

  // Local Data Access
  /**
   * get the current object from the state
   * @param {object} state the current redux state
   * @returns {object} the object
   */
  getObject(state, queryName) {
    return Access.getObject(state, this.path, queryName);
  }

  /**
   * @param {object} state current redux state
   * @param {object} opts
   * @returns {object} table of the results
   */
  getTable(state, opts, queryName) {
    return Access.getTable(state, this.path, opts, queryName);
  }

  /**
   * @param {object} state the current redux state
   * @returns {object} the model for the object
   */
  getModel(state, queryName) {
    return Access.getObject(state, `${this.path}.model`, queryName);
  }

  /**
   * gets the results of a specific query
   * @param {object} state the current redux state
   * @param {string} queryName identifier of the query
   * @returns {object} the results of the query
   */
  getHistory(state, queryName, opts) {
    return Access.getHistory(state, `${this.path}.history`, queryName, opts);
  }
  getEvents(state, queryName, opts) {
    return Access.getHistory(state, `${this.path}.events`, queryName, opts);
  }

  /**
   * @param {object} state the current redux state
   * @returns {boolean} whether or not the results can be returned as a table
   */
  isTable(state, opts, queryName) {
    return Access.isTable(state, this.path, opts, queryName);
  }

  /**
   * @param {object} state the current redux state
   * @returns {boolean} whether the query is loading
   */
  isLoading(state, queryName) {
    return Access.isLoading(state, this.path, queryName);
  }

  /**
   * @param {object} state the current redux state
   * @returns {boolean} whether the query had an error
   */
  isError(state, queryName) {
    return Access.isError(state, this.path, queryName);
  }

  /**
   * @param {object} state the current redux state
   * @returns {boolean} whether the query is finished
   */
  isDone(state, queryName) {
    return Access.isDone(state, this.path, queryName);
  }

  /**
   * @param {object} state the current redux state
   * @returns {Error} an error if one exists, otherwise undefined
   */
  getError(state, queryName) {
    return Access.getError(state, this.path, queryName);
  }

  /**
   * @param {object} state the current redux state
   * @returns {boolean} whether the query is loading
   */
  isHistoryLoading(state, queryName) {
    return Access.isLoading(state, `${this.path}.history`, queryName);
  }

  /**
   * @param {object} state the current redux state
   * @returns {boolean} whether the query had an error
   */
  isHistoryError(state, queryName) {
    return Access.isError(state, `${this.path}.history`, queryName);
  }

  /**
   * @param {object} state the current redux state
   * @returns {boolean} whether the query is finished
   */
  isHistoryDone(state, queryName) {
    return Access.isDone(state, `${this.path}.history`, queryName);
  }

  /**
   * @param {object} state the current redux state
   * @returns {Error} an error if one exists, otherwise undefined
   */
  getHistoryError(state, queryName) {
    return Access.getError(state, `${this.path}.history`, queryName);
  }

  /**
   * @param {object} state the current redux state
   * @returns {boolean} whether the query is loading
   */
  isEventsLoading(state, queryName) {
    return Access.isLoading(state, `${this.path}.events`, queryName);
  }

  /**
   * @param {object} state the current redux state
   * @returns {boolean} whether the query had an error
   */
  isEventsError(state, queryName) {
    return Access.isError(state, `${this.path}.events`, queryName);
  }

  /**
   * @param {object} state the current redux state
   * @returns {boolean} whether the query is finished
   */
  isEventsDone(state, queryName) {
    return Access.isDone(state, `${this.path}.events`, queryName);
  }

  /**
   * @param {object} state the current redux state
   * @returns {Error} an error if one exists, otherwise undefined
   */
  getEventsError(state, queryName) {
    return Access.getError(state, `${this.path}.events`, queryName);
  }
}
function createObj(path, id, isInGroup) {
  return new Obj(path, id, isInGroup);
}
export default {
  create: createObj
};